/**
 * One filter row, above everything it scopes.
 *
 * Never per-card and never inside a chart: every tile, card, chart and table below
 * re-renders against the same slice, so the numbers on screen always agree with each
 * other. The time window comes first because it is the control a reader reaches for
 * before any other.
 */

import { LayoutGrid, RotateCcw, Table2 } from 'lucide-react'

import type { TargetResult } from '../api/types'
import { cn } from '../lib/cn'

/** Window presets, as sample counts - the backend's window is bounded by samples. */
export const WINDOW_PRESETS = [
  { id: 'all', label: 'Full window', samples: null },
  { id: 'last-15', label: 'Last 15', samples: 15 },
  { id: 'last-60', label: 'Last 60', samples: 60 },
  { id: 'last-180', label: 'Last 180', samples: 180 },
] as const

/**
 * The health slices, worst-first after "All".
 *
 * `attention` is the one most people want and the default reason to touch this control:
 * everything that is not plainly healthy, *or* that reported an issue - the union, not
 * the intersection, because a target can carry a failing check while its own declared
 * status still reads healthy, and a filter called "show me the problems" that hides
 * those would be lying. The two narrower slices exist for the other question, which is
 * "show me only the ones that are actually down" during an incident with a long list.
 */
export const HEALTH_FILTERS = [
  { id: 'all', label: 'All', hint: 'Every target.' },
  {
    id: 'attention',
    label: 'Unhealthy & issues',
    hint: 'Anything not healthy, or reporting a failing check or an issue.',
  },
  {
    id: 'unhealthy',
    label: 'Unhealthy only',
    hint: 'Unhealthy or unreachable - a critical dependency failed, or nothing answered.',
  },
  { id: 'degraded', label: 'Degraded', hint: 'A non-critical check failed.' },
  {
    id: 'healthy',
    label: 'Healthy',
    hint: 'Every check passed and nothing was reported - the exact complement of '
      + '"Unhealthy & issues".',
  },
] as const

export type WindowPresetId = (typeof WINDOW_PRESETS)[number]['id']
export type HealthFilterId = (typeof HEALTH_FILTERS)[number]['id']
export type ViewMode = 'cards' | 'table'

export interface Filters {
  /** Selected chip ids - each matches a group of targets (see FilterChip.matches). */
  siteIds: string[]
  health: HealthFilterId
  window: WindowPresetId
  view: ViewMode
}

/**
 * One selectable filter chip. Configured sites and manually added hosts both become
 * chips, in their own groups - so a manual host is filterable, not buried under a
 * default site's chip or absent because it has no site chip of its own.
 */
export interface FilterChip {
  id: string
  label: string
  group: 'configured' | 'manual'
  /** Whether a given target belongs to this chip. */
  matches: (target: { site_id: string; target_id: string }) => boolean
}

interface FilterBarProps {
  chips: FilterChip[]
  filters: Filters
  onChange: (filters: Filters) => void
  /** How many targets survive the current filters, so an empty grid is explicable. */
  visibleCount: number
  totalCount: number
  /** Set when cards have been dragged out of their default order. */
  customOrder?: boolean
  onResetOrder?: () => void
}

export function FilterBar({
  chips,
  filters,
  onChange,
  visibleCount,
  totalCount,
  customOrder,
  onResetOrder,
}: FilterBarProps) {
  const toggle = (chipId: string) => {
    const next = filters.siteIds.includes(chipId)
      ? filters.siteIds.filter((id) => id !== chipId)
      : [...filters.siteIds, chipId]
    onChange({ ...filters, siteIds: next })
  }

  const configured = chips.filter((c) => c.group === 'configured')
  const manual = chips.filter((c) => c.group === 'manual')
  const allSites = filters.siteIds.length === 0

  return (
    <div className="card flex flex-wrap items-center gap-x-5 gap-y-3 px-4 py-3">
      {/* Time window first. */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-ink-secondary">Window</span>
        <div className="flex overflow-hidden rounded-md border">
          {WINDOW_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              aria-pressed={filters.window === preset.id}
              onClick={() => onChange({ ...filters, window: preset.id })}
              className={cn(
                'px-2.5 py-1 text-xs transition-colors',
                filters.window === preset.id
                  ? 'bg-accent-soft font-semibold text-ink'
                  : 'text-ink-secondary hover:bg-surface-sunken',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-ink-secondary">Sites</span>
        <button
          type="button"
          aria-pressed={allSites}
          onClick={() => onChange({ ...filters, siteIds: [] })}
          className={cn(
            'rounded-full border px-2.5 py-1 text-xs transition-colors',
            allSites
              ? 'bg-accent-soft font-semibold text-ink'
              : 'text-ink-secondary hover:bg-surface-sunken',
          )}
        >
          All
        </button>

        {configured.map((chip) => (
          <ChipButton
            key={chip.id}
            chip={chip}
            active={filters.siteIds.includes(chip.id)}
            onToggle={toggle}
          />
        ))}

        {manual.length > 0 ? (
          <>
            {/* A thin divider, then the manual hosts in their own group, so an operator
                can filter to a host they added by hand rather than only the built-ins. */}
            <span aria-hidden className="mx-1 h-4 w-px bg-[var(--color-grid)]" />
            <span className="text-xs font-medium text-ink-secondary">Manual</span>
            {manual.map((chip) => (
              <ChipButton
                key={chip.id}
                chip={chip}
                active={filters.siteIds.includes(chip.id)}
                onToggle={toggle}
              />
            ))}
          </>
        ) : null}
      </div>

      {/*
        Health, as a segmented control rather than a "problems only" checkbox. A boolean
        answers one question; during an incident the question is usually narrower than
        "is anything wrong" - it is "which ones are actually down" - and a checkbox has
        no way to say that.
      */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-ink-secondary">Health</span>
        <div className="flex overflow-hidden rounded-md border">
          {HEALTH_FILTERS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              title={preset.hint}
              aria-pressed={filters.health === preset.id}
              onClick={() => onChange({ ...filters, health: preset.id })}
              className={cn(
                'px-2.5 py-1 text-xs transition-colors',
                filters.health === preset.id
                  ? 'bg-accent-soft font-semibold text-ink'
                  : 'text-ink-secondary hover:bg-surface-sunken',
              )}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ml-auto flex items-center gap-3">
        {/*
          Only once the order has actually been changed - a reset button for a thing
          nobody has done yet is a control that has to be read and dismissed every time.
        */}
        {customOrder && onResetOrder ? (
          <button
            type="button"
            onClick={onResetOrder}
            title="Put the cards back in their automatic order: grouped by site, worst status first."
            className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-ink-secondary transition-colors hover:bg-surface-sunken"
          >
            <RotateCcw size={12} aria-hidden />
            Reset card order
          </button>
        ) : null}

        <span className="tabular text-xs text-ink-muted">
          {visibleCount} of {totalCount} shown
        </span>

        {/* The table view is the accessible twin of every chart above it. */}
        <div className="flex overflow-hidden rounded-md border">
          {(
            [
              { id: 'cards' as const, label: 'Cards', icon: LayoutGrid },
              { id: 'table' as const, label: 'Table', icon: Table2 },
            ]
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              aria-pressed={filters.view === id}
              onClick={() => onChange({ ...filters, view: id })}
              className={cn(
                'inline-flex items-center gap-1.5 px-2.5 py-1 text-xs transition-colors',
                filters.view === id
                  ? 'bg-accent-soft font-semibold text-ink'
                  : 'text-ink-secondary hover:bg-surface-sunken',
              )}
            >
              <Icon size={13} aria-hidden />
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

function ChipButton({
  chip,
  active,
  onToggle,
}: {
  chip: FilterChip
  active: boolean
  onToggle: (id: string) => void
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onToggle(chip.id)}
      className={cn(
        'rounded-full border px-2.5 py-1 text-xs transition-colors',
        active
          ? 'bg-accent-soft font-semibold text-ink'
          : 'text-ink-secondary hover:bg-surface-sunken',
      )}
    >
      {chip.label}
    </button>
  )
}

/** Trim a window to the selected preset. */
export function applyWindow<T>(samples: T[], window: WindowPresetId): T[] {
  const preset = WINDOW_PRESETS.find((entry) => entry.id === window)
  if (!preset?.samples) return samples
  return samples.slice(-preset.samples)
}

/**
 * Whether a target is reporting something wrong at check level.
 *
 * Two sources, because they can disagree: the normalised `services` list (a check whose
 * `ok` came back false) and the payload's own top-level `issues` map, which may name
 * something that is not one of the listed checks. Either one is an issue, and a target
 * can carry one while its declared status still reads healthy.
 */
export function hasIssues(result: TargetResult): boolean {
  return (
    Object.keys(result.issues).length > 0 ||
    result.services.some((service) => service.ok === false)
  )
}

/**
 * Whether a target belongs in the selected health slice.
 *
 * `healthy` and `attention` are exact complements - every target is in one or the
 * other - which is what lets the hero figure read "N of M" against the same rule the
 * grid filtered by.
 */
export function matchesHealth(result: TargetResult, health: HealthFilterId): boolean {
  switch (health) {
    case 'attention':
      return result.status !== 'healthy' || hasIssues(result)
    case 'healthy':
      return result.status === 'healthy' && !hasIssues(result)
    case 'unhealthy':
      return result.status === 'unhealthy' || result.status === 'unreachable'
    case 'degraded':
      return result.status === 'degraded'
    default:
      return true
  }
}

/**
 * Whether a target passes the selected chips (empty selection = all).
 *
 * A chip matches by its own predicate, so a configured-site chip matches every target
 * under that site and a manual chip matches its one host - the two kinds coexist in one
 * selection.
 */
export function matchesChips(
  target: { site_id: string; target_id: string },
  selectedIds: string[],
  chips: FilterChip[],
): boolean {
  if (selectedIds.length === 0) return true
  return chips.some(
    (chip) => selectedIds.includes(chip.id) && chip.matches(target),
  )
}
